import { useRef, useState } from 'react';
import { JotlyMark } from '../components/JotlyMark';
import { sendMessage } from '../api/client';

const ONBOARDED_KEY = 'jotly.onboarded';
export const PENDING_CAPTURE_KEY = 'jotly.pendingCapture';

const STEPS = [
  { n: '1', bold: 'Fling it in.', rest: ' A line, a mumble, a forwarded message.' },
  { n: '2', bold: 'I sort it.', rest: ' Type, timing, where it belongs — quietly.' },
  { n: '3', bold: 'Ask me later', rest: ', however you half-remember it.' },
];

export function OnboardingScreen({ onComplete }: { onComplete: () => void }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [phase, setPhase] = useState<'input' | 'ack'>('input');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  async function submit() {
    const trimmed = text.trim();
    if (!trimmed || sending) return;

    setSending(true);
    setText('');

    try {
      await sendMessage(trimmed);
    } catch {
      // Not signed in yet — stash locally so it syncs after sign-in.
      try {
        const pending = JSON.parse(localStorage.getItem(PENDING_CAPTURE_KEY) ?? '[]');
        pending.push({ text: trimmed, queuedAt: new Date().toISOString() });
        localStorage.setItem(PENDING_CAPTURE_KEY, JSON.stringify(pending));
      } catch { /* storage full — the jot is lost, but onboarding still completes */ }
    }

    setPhase('ack');
    await new Promise(r => setTimeout(r, 1400));
    localStorage.setItem(ONBOARDED_KEY, '1');
    onComplete();
  }

  function skip() {
    localStorage.setItem(ONBOARDED_KEY, '1');
    onComplete();
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  const hasText = text.trim().length > 0;

  return (
    <div style={styles.page}>
      <div style={styles.content}>
        <div style={styles.mark}>
          <JotlyMark size={32} />
          <span style={styles.wordmark}>Jotly</span>
          <span style={styles.dot} />
        </div>

        {phase === 'ack' ? (
          <div style={styles.ackWrap} className="ob-fade-in">
            <div style={styles.ackDot}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2 style={styles.ackTitle}>Jotted &amp; safe</h2>
            <p style={styles.ackSub}>
              I&rsquo;ll sort the type and timing. You can let it go.
            </p>
          </div>
        ) : (
          <>
            <h2 style={styles.hero}>
              Nothing here yet &mdash;<br />and that&rsquo;s the point.
            </h2>

            <p style={styles.lead}>
              Fling me the next thought rattling around. Don&rsquo;t tidy it,
              don&rsquo;t sort it &mdash; I&rsquo;ll handle that, and hand
              it back when you need it.
            </p>

            <div style={styles.steps}>
              {STEPS.map((step, i) => (
                <div
                  key={step.n}
                  style={{
                    ...styles.step,
                    ...(i > 0 ? styles.stepBorder : {}),
                  }}
                >
                  <div style={styles.stepNum}>{step.n}</div>
                  <span style={styles.stepText}>
                    <strong style={styles.stepBold}>{step.bold}</strong>
                    {step.rest}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {phase === 'input' && (
        <div style={styles.composerWrap}>
          <div style={styles.composerBox}>
            <textarea
              ref={inputRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Try it — jot anything…"
              style={styles.composerInput}
              rows={1}
              disabled={sending}
              autoFocus
            />
            <button
              type="button"
              style={{
                ...styles.sendBtn,
                ...(!hasText || sending ? styles.sendBtnDisabled : {}),
              }}
              onClick={submit}
              disabled={!hasText || sending}
              aria-label="Send"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 19V5M5 12l7-7 7 7" />
              </svg>
            </button>
          </div>
          <button
            type="button"
            style={styles.skipBtn}
            onClick={skip}
          >
            Skip for now
          </button>
        </div>
      )}

      <style>{`
        @keyframes ob-fade { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .ob-fade-in { animation: ob-fade 280ms ease-out forwards; }
      `}</style>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    background: 'var(--rc-bg)',
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0 32px',
  },
  mark: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    marginBottom: 26,
  },
  wordmark: {
    fontFamily: 'var(--rc-display)',
    fontSize: 28,
    fontWeight: 600,
    letterSpacing: '-0.02em',
    color: 'var(--rc-ink)',
  },
  dot: {
    width: 9,
    height: 9,
    borderRadius: '50%',
    background: 'var(--rc-accent)',
  },
  hero: {
    fontFamily: 'var(--rc-display)',
    fontSize: 27,
    fontWeight: 600,
    lineHeight: '1.24',
    letterSpacing: '-0.02em',
    color: 'var(--rc-ink)',
    textAlign: 'center',
    margin: '0 0 16px',
  },
  lead: {
    fontFamily: 'var(--rc-sans)',
    fontSize: 16,
    lineHeight: '1.55',
    color: 'var(--rc-ink-2)',
    textAlign: 'center',
    maxWidth: 340,
    margin: 0,
  },
  steps: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 30,
    maxWidth: 340,
    width: '100%',
  },
  step: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '13px 0',
  },
  stepBorder: {
    borderTop: '1px solid var(--rc-border)',
  },
  stepNum: {
    width: 26,
    height: 26,
    borderRadius: '50%',
    background: 'var(--rc-accent-soft)',
    color: 'var(--rc-accent-ink)',
    fontFamily: 'var(--rc-mono)',
    fontSize: 13,
    fontWeight: 700,
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
  },
  stepText: {
    fontFamily: 'var(--rc-sans)',
    fontSize: 14.5,
    lineHeight: '1.4',
    color: 'var(--rc-ink)',
  },
  stepBold: {
    fontWeight: 600,
  },
  composerWrap: {
    padding: '6px 32px 32px',
    maxWidth: 480,
    width: '100%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
  },
  composerBox: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 9,
    background: 'var(--rc-card)',
    border: '1px solid var(--rc-border)',
    borderRadius: 24,
    paddingLeft: 17,
    paddingRight: 6,
    paddingTop: 6,
    paddingBottom: 6,
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    width: '100%',
  },
  composerInput: {
    flex: 1,
    fontFamily: 'var(--rc-sans)',
    fontSize: 15.5,
    lineHeight: '22px',
    color: 'var(--rc-ink)',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    padding: '9px 0',
    resize: 'none',
    maxHeight: 96,
    minHeight: 22,
  },
  sendBtn: {
    width: 38,
    height: 38,
    borderRadius: 19,
    background: 'var(--rc-accent)',
    border: 'none',
    display: 'grid',
    placeItems: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  },
  sendBtnDisabled: {
    background: 'var(--rc-ink-3)',
    cursor: 'default',
  },
  skipBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '12px 16px',
    fontFamily: 'var(--rc-sans)',
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--rc-ink-3)',
  },
  ackWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 14,
  },
  ackDot: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    background: '#15803d',
    display: 'grid',
    placeItems: 'center',
    marginBottom: 4,
  },
  ackTitle: {
    fontFamily: 'var(--rc-display)',
    fontSize: 24,
    fontWeight: 600,
    letterSpacing: '-0.02em',
    color: 'var(--rc-ink)',
    margin: 0,
  },
  ackSub: {
    fontFamily: 'var(--rc-sans)',
    fontSize: 16,
    lineHeight: '1.5',
    color: 'var(--rc-ink-2)',
    textAlign: 'center',
    maxWidth: 280,
    margin: 0,
  },
};
